import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { Account, DashboardService } from '@treasury/domain/dashboard';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import { plusIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-footer';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './add-accounts-to-group-menu';
import { type AccountGroup } from './configure-account-groups-sheet';
import './order-group-accounts-table';
import './rename-accounts-group-menu';

export const tagName = 'configure-group-accounts-sheet';

@customElement(tagName)
export class ConfigureGroupAccountsSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public open = false;

    @property({ type: Object })
    public accountGroup?: AccountGroup;

    @InjectProperty()
    declare private dashboardService: DashboardService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @state()
    private loading = false;

    protected render() {
        return html`<tm-bottom-sheet
            horizontal
            .open=${this.open}
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            ${this.renderSheetContent()}
        </tm-bottom-sheet>`;
    }

    private async updateAccountIndex(account: Account, index: number) {
        if (!this.accountGroup) {
            throw new Error('Could not update account index. No account group');
        }

        this.loading = true;
        try {
            await this.dashboardService.updateAccountOrder(this.accountGroup.id, account, index);
            this.dispatchEvent(
                new CustomEvent('groupAccountOrderUpdated', { bubbles: true, composed: true })
            );
            this.notificationService.renderSuccess('Account order updated');
        } catch {
            this.notificationService.renderError('Failed to update account order');
        } finally {
            this.loading = false;
        }
    }

    private async removeAccountFromGroup(account: Account) {
        this.loading = true;
        try {
            if (!this.accountGroup) {
                throw new Error('No account group');
            }
            await this.dashboardService.removeAccountFromGroup(this.accountGroup.id, account);
            this.dispatchEvent(
                new CustomEvent('accountRemoved', {
                    detail: {
                        account,
                    },
                })
            );
            this.notificationService.renderSuccess('Account removed');
        } catch (e: unknown) {
            if (e instanceof Error) {
                this.notificationService.renderError(e.message);
            } else {
                this.notificationService.renderError('An unknown error occurred');
            }
        } finally {
            this.loading = false;
        }
    }

    private async renameGroup(newGroupName: string) {
        if (!this.accountGroup) {
            return;
        }
        this.loading = true;
        try {
            await this.dashboardService.updateAccountGroupName(this.accountGroup.id, {
                name: newGroupName,
                id: this.accountGroup.id,
                accounts: this.accountGroup.accounts.map(account => account.toDto()),
                updatedDate: new Date().toISOString(),
            });
            this.notificationService.renderSuccess('Group name updated');
            this.accountGroup.name = newGroupName;
            this.dispatchEvent(
                new CustomEvent('groupNameUpdated', { bubbles: true, composed: true })
            );
        } catch (e: unknown) {
            if (e instanceof Error) {
                this.notificationService.renderError(e.message);
            } else {
                this.notificationService.renderError('Failed to update group name');
            }
        } finally {
            this.loading = false;
        }
    }

    private renderSheetContent() {
        if (!this.accountGroup) {
            return nothing;
        }
        return html`<div slot="header-center" class="flex flex-col">
                <span>${this.accountGroup.name}</span
                ><span class="text-xs">Drag & drop to reorder</span>
            </div>
            <div slot="header-right">
                <tm-button
                    importance="tertiary"
                    @click=${() => {
                        this.open = false;
                        this.dispatchEvent(new CustomEvent('addAccounts'));
                    }}
                    ><tm-icon .icon=${plusIcon} color="#fff"></tm-icon
                ></tm-button>
            </div>
            <div id="order-group-accounts-table">
                <div id="order-group-accounts-table" class="mt-5 px-2">
                    ${this.open
                        ? html`<order-group-accounts-table
                              .accounts=${this.accountGroup.accounts}
                              @account-removed=${(e: CustomEvent) => {
                                  const { account } = e.detail;
                                  this.removeAccountFromGroup(account);
                              }}
                              @accounts-reordered=${(e: CustomEvent) => {
                                  if (!this.accountGroup) {
                                      return;
                                  }
                                  this.accountGroup.accounts = e.detail.accounts;
                                  this.updateAccountIndex(
                                      e.detail.updatedAccount,
                                      e.detail.newIndex
                                  );
                              }}
                          ></order-group-accounts-table>`
                        : nothing}
                </div>
                <div
                    class="absolute bottom-0 w-full flex items-center justify-center py-2 bg-primary"
                >
                    <rename-accounts-group-menu
                        .groupName=${this.accountGroup.name}
                        @renameGroup=${({ detail }: CustomEvent) => {
                            this.renameGroup(detail);
                        }}
                    ></rename-accounts-group-menu>
                </div>
            </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: ConfigureGroupAccountsSheet;
    }
}
